import { createContext, useContext, ReactNode } from 'react'
import { useSuspenseQuery } from '@tanstack/react-query'
import { featureFlagsQueryOptions } from '@/api/feature-flags/get'
import { FeatureFlagName } from '@/api/feature-flags/get.schema'

interface FeatureFlagsContextType {
  isEnabled: (flagName: FeatureFlagName) => boolean
  flags: Record<string, boolean>
  isLoading: boolean
}

const FeatureFlagsContext = createContext<FeatureFlagsContextType | undefined>(
  undefined
)

export function FeatureFlagsProvider({ children }: { children: ReactNode }) {
  const { data, isLoading } = useSuspenseQuery(featureFlagsQueryOptions())

  const isEnabled = (flagName: FeatureFlagName): boolean => {
    return data.flags[flagName] ?? false // Default to false for safety
  }

  return (
    <FeatureFlagsContext.Provider
      value={{
        isEnabled,
        flags: data.flags,
        isLoading,
      }}
    >
      {children}
    </FeatureFlagsContext.Provider>
  )
}

export function useFeatureFlags() {
  const context = useContext(FeatureFlagsContext)
  if (context === undefined) {
    throw new Error(
      'useFeatureFlags must be used within a FeatureFlagsProvider'
    )
  }
  return context
}

// Convenience hook for checking single flags
export function useFeatureFlag(flagName: FeatureFlagName): boolean {
  const { isEnabled } = useFeatureFlags()
  return isEnabled(flagName)
}

// Custom hook for conditional rendering
export function useConditionalRender(flagName: FeatureFlagName) {
  const isEnabled = useFeatureFlag(flagName)

  return (component: ReactNode) => (isEnabled ? component : null)
}
